<template>
    <section class="litigation-details">
  
      <div class="details-toolbar">
        <div class="toolbar-controls">
          <label class="litigation-search">
            <span class="material-icons-outlined" aria-hidden="true">
              search
            </span>

            <input
              v-model="searchText"
              type="search"
              placeholder="Search litigation name"
              aria-label="Search litigations by name"
            />
          </label>

          <select
            v-model="statusFilter"
            class="status-filter"
            aria-label="Filter litigations by status"
          >
            <option value="">
              All statuses
            </option>

            <option value="inReview">
              In Review
            </option>

            <option value="draft">
              Draft
            </option>

            <option value="published">
              Published
            </option>

            <option value="archived">
              Archived
            </option>
          </select>

          <button
            type="button"
            class="refresh-button"
            :disabled="loading"
            @click="loadLitigations"
          >
            {{ loading ? "Loading..." : "Refresh" }}
          </button>
        </div>
  
      </div>
  
      <div
        v-if="loading"
        class="state-card"
      >
        Loading litigations...
      </div>
  
      <div
        v-else-if="errorMessage"
        class="state-card error"
      >
        {{ errorMessage }}
      </div>
  
      <div
        v-else-if="litigations.length === 0"
        class="state-card"
      >
        No litigation records were found.
      </div>

      <div
        v-else-if="sortedLitigations.length === 0"
        class="state-card"
      >
        No litigations match the current search and status filter.
      </div>
  
      <div
        v-else
        class="litigation-table-wrapper"
      >
        <table class="litigation-table">
  
          <thead>
            <tr>
              <th>Litigation</th>
              <th>Status</th>
              <th>Questions</th>
              <th>Submissions</th>
              <th>Actions</th>
            </tr>
          </thead>
  
          <tbody>
            <tr
              v-for="litigation in sortedLitigations"
              :key="litigation.id"
            >
              <td>
                <div class="litigation-name">
                  {{ litigation.publicActionName }}
                </div>

                <div class="litigation-description">
                  {{ litigation.internalName }}
                </div>
  
                <div class="litigation-description">
                  {{ shortenedDescription(litigation.description) }}
                </div>
  
              </td>
  
              <td>
                <span
                  class="status-pill"
                  :class="{
                    live:
                      litigation.publicationStatus === 'published',
                    inactive:
                      litigation.publicationStatus !== 'published'
                  }"
                >
                  {{
                    publicationStatusLabel(
                      litigation.publicationStatus
                    )
                  }}
                </span>
              </td>
  
              <td>
                {{
                  litigation.questions.length
                }}
              </td>
  
              <td>
                {{
                  litigation.submissionCount
                }}
              </td>

              <td>
                <button
                    type="button"
                    class="litigation-action-button"
                    :class="litigationActionClass(litigation.publicationStatus)"
                    @click="$emit('edit', litigation)"
                >
                    {{ litigationActionLabel(litigation.publicationStatus) }}
                </button>
                </td>
            </tr>
          </tbody>
  
        </table>
      </div>
  
    </section>
  </template>
  
  <script>
  import {
    computed,
    onMounted,
    ref,
    watch
  } from "vue";
  
  import {
    fetchLitigations
  } from "@/firebase/firestore";
  
  export default {
    name:
      "LitigationDetails",
  
    props: {
      refreshKey: {
        type:
          Number,
        default:
          0
      }
    },

    emits: [
        "edit"
        ],
  
    setup(props) {
  
      const litigations =
        ref([]);
  
      const loading =
        ref(false);
  
      const errorMessage =
        ref("");

      const searchText =
        ref("");

      const statusFilter =
        ref("");

      const sortedLitigations =
        computed(() => {
          const search =
            searchText.value
              .trim()
              .toLowerCase();

          return litigations.value
            .filter(litigation => {
              if (
                statusFilter.value &&
                litigation.publicationStatus !== statusFilter.value
              ) {
                return false;
              }

              if (!search) {
                return true;
              }

              return [
                litigation.publicActionName,
                litigation.internalName
              ]
                .join(" ")
                .toLowerCase()
                .includes(search);
            })
            .sort((first, second) => {

              const statusPriority = {
                inReview: 0,
                draft: 1
              };

              const firstPriority =
                statusPriority[first.publicationStatus] ?? 2;

              const secondPriority =
                statusPriority[second.publicationStatus] ?? 2;

              if (firstPriority !== secondPriority) {
                return firstPriority - secondPriority;
              }

              const firstName =
                String(
                  first.publicActionName ||
                  first.internalName ||
                  ""
                ).trim();

              const secondName =
                String(
                  second.publicActionName ||
                  second.internalName ||
                  ""
                ).trim();

              const firstWord =
                firstName.split(/\s+/)[0];

              const secondWord =
                secondName.split(/\s+/)[0];

              const firstWordComparison =
                firstWord.localeCompare(
                  secondWord,
                  undefined,
                  {
                    sensitivity: "base",
                    numeric: true
                  }
                );

              if (firstWordComparison !== 0) {
                return firstWordComparison;
              }

              return firstName.localeCompare(
                secondName,
                undefined,
                {
                  sensitivity: "base",
                  numeric: true
                }
              );
            });
        });

      const litigationActionLabel =
        status => {

          if (status === "draft") {
            return "Draft";
          }

          if (status === "inReview") {
            return "Review";
          }

          return "Edit";
        };

      const litigationActionClass =
        status => {

          if (status === "draft") {
            return "action-draft";
          }

          if (status === "inReview") {
            return "action-review";
          }

          return "action-edit";
        };
  
      const loadLitigations =
        async () => {
  
          loading.value =
            true;
  
          errorMessage.value =
            "";
  
          try {
  
            litigations.value =
              await fetchLitigations();
  
          } catch (error) {
  
            console.error(
              "❌ Unable to load litigations:",
              error
            );
  
            errorMessage.value =
              "Unable to load litigation records.";
  
          } finally {
  
            loading.value =
              false;
          }
        };
  
      const publicationStatusLabel =
        status => {

          switch (status) {
          case "inReview":
            return "In Review";

          case "published":
            return "Published";

          case "archived":
            return "Archived";

          default:
            return "Draft";
          }
        };
  
      const shortenedDescription =
        description => {
  
          const value =
            String(
              description ?? ""
            );
  
          if (
            value.length <=
            55
          ) {
  
            return value;
          }
  
          return `${value.slice(
            0,
            52
          )}...`;
        };
  
      onMounted(
        loadLitigations
      );
  
      watch(
        () =>
          props.refreshKey,
        loadLitigations
      );
  
      return {
        litigations,
        sortedLitigations,
        loading,
        errorMessage,
        searchText,
        statusFilter,
        loadLitigations,
        litigationActionLabel,
        litigationActionClass,
        publicationStatusLabel,
        shortenedDescription
      };
    }
  };
  </script>
  
  <style scoped>
  .litigation-details {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
  
  .details-toolbar {
    display: flex;
    align-items: center;
    width: 100%;
  }

  .toolbar-controls {
    display: flex;
    align-items: center;
    width: 100%;
    gap: 10px;
  }

  .litigation-search {
    display: flex;
    align-items: center;
    min-width: 220px;
    flex: 1;
    padding: 0 11px;
    border: 1px solid #d7ded9;
    border-radius: 9px;
    background: #ffffff;
    color: #87908b;
  }

  .litigation-search .material-icons-outlined {
    flex-shrink: 0;
    font-size: 20px;
  }

  .litigation-search input {
    min-width: 0;
    width: 100%;
    padding: 9px 8px;
    border: 0;
    outline: 0;
    background: transparent;
    color: #25322c;
    font: inherit;
  }

  .status-filter {
    min-width: 145px;
    padding: 9px 11px;
    border: 1px solid #d7ded9;
    border-radius: 9px;
    background: #ffffff;
    color: #25322c;
    font: inherit;
  }

  .litigation-action-button {
    width: 82px;
    padding: 8px 13px;
    box-sizing: border-box;
    border: 1px solid transparent;
    border-radius: 8px;
    font: inherit;
    font-weight: 600;
    cursor: pointer;
    transition:
      background-color 0.18s ease,
      border-color 0.18s ease;
  }

  .litigation-action-button.action-draft {
    border-color: rgba(195, 45, 58, 0.35);
    background: rgba(220, 53, 69, 0.11);
    color: #a33636;
  }

  .litigation-action-button.action-draft:hover {
    background: rgba(220, 53, 69, 0.18);
  }

  .litigation-action-button.action-review {
    border-color: rgba(224, 112, 20, 0.35);
    background: rgba(243, 126, 32, 0.12);
    color: #ad5514;
  }

  .litigation-action-button.action-review:hover {
    background: rgba(243, 126, 32, 0.2);
  }

  .litigation-action-button.action-edit {
    border-color: rgba(38, 160, 93, 0.3);
    background: rgba(38, 160, 93, 0.08);
    color: #178554;
  }

  .litigation-action-button.action-edit:hover {
    background: rgba(38, 160, 93, 0.15);
  }
  
  .refresh-button {
    padding: 9px 15px;
    border: 1px solid #d7ded9;
    border-radius: 9px;
    background: #ffffff;
    color: #25322c;
    font: inherit;
    font-weight: 600;
    cursor: pointer;
  }
  
  .refresh-button:hover:not(:disabled) {
    background: #f4f8f5;
  }
  
  .refresh-button:disabled {
    cursor: wait;
    opacity: 0.65;
  }
  
  .state-card {
    padding: 24px;
    border: 1px solid #e2e7e4;
    border-radius: 12px;
    background: #ffffff;
    color: #737d78;
  }
  
  .state-card.error {
    border-color: #efc5c5;
    background: #fff7f7;
    color: #a33636;
  }
  
  .litigation-table-wrapper {
    overflow-x: auto;
    border: 1px solid #e2e7e4;
    border-radius: 12px;
    background: #ffffff;
  }
  
  .litigation-table {
    width: 100%;
    border-collapse: collapse;
  }
  
  .litigation-table th,
  .litigation-table td {
    padding: 16px 18px;
    border-bottom: 1px solid #edf0ee;
    text-align: left;
    vertical-align: top;
  }
  
  .litigation-table th {
    background: #f7f9f8;
    color: #59645f;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
  
  .litigation-table tbody tr:last-child td {
    border-bottom: 0;
  }
  
  .litigation-table tbody tr:hover {
    background: #fbfdfb;
  }
  
  .litigation-name {
    color: #1c2923;
    font-weight: 600;
  }
  
  .litigation-description {
    max-width: 520px;
    margin-top: 4px;
    color: #68736d;
    font-size: 0.88rem;
    line-height: 1.4;
  }
  
  .status-pill {
    display: inline-flex;
    padding: 5px 10px;
    border-radius: 999px;
    font-size: 0.8rem;
    font-weight: 600;
  }
  
  .status-pill.live {
    background: #edf7f1;
    color: #178554;
  }
  
  .status-pill.inactive {
    background: #f3f3f3;
    color: #757575;
  }
  
  @media (max-width: 700px) {
    .details-toolbar {
      align-items: flex-start;
      flex-direction: column;
    }

    .toolbar-controls {
      align-items: stretch;
      width: 100%;
      flex-direction: column;
    }

    .litigation-search,
    .status-filter {
      width: 100%;
      box-sizing: border-box;
    }
  
    .refresh-button {
      width: 100%;
    }
  }
  </style>
